import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationToast = ({ 
  notifications = [], 
  message,
  type = 'info',
  onDismiss,
  onClose,
  position = 'top-right',
  autoHideDuration = 5000 
}) => {
  const [visibleNotifications, setVisibleNotifications] = useState([]);
  const timersRef = useRef(new Map());
  const prevMessageRef = useRef(null);
  const prevTypeRef = useRef(null);
  const prevNotificationsRef = useRef(null);
  const handleDismissRef = useRef(null);

  // Support both array and single notification props
  useEffect(() => {
    // Check if message actually changed
    if (message && (message !== prevMessageRef.current || type !== prevTypeRef.current)) {
      prevMessageRef.current = message;
      prevTypeRef.current = type;
      
      // Single notification mode
      const notification = {
        id: Date.now().toString(),
        message,
        type,
        timestamp: new Date().toISOString()
      };
      setVisibleNotifications([notification]);
    } else if (notifications && notifications.length > 0) {
      // Check if notifications array actually changed
      const notificationsStr = JSON.stringify(notifications);
      const prevNotificationsStr = prevNotificationsRef.current ? JSON.stringify(prevNotificationsRef.current) : null;
      
      if (notificationsStr !== prevNotificationsStr) {
        prevNotificationsRef.current = notifications;
        // Array mode
        setVisibleNotifications(notifications);
      }
    } else if (!message && (!notifications || notifications.length === 0)) {
      // Clear notifications if both are empty
      if (visibleNotifications.length > 0) {
        setVisibleNotifications([]);
      }
      prevMessageRef.current = null;
      prevTypeRef.current = null;
      prevNotificationsRef.current = null;
    }
  }, [notifications, message, type, visibleNotifications.length]);

  // Auto-dismiss notifications
  useEffect(() => {
    // Clear all existing timers
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();

    if (autoHideDuration > 0 && visibleNotifications.length > 0) {
      visibleNotifications
        .filter(notification => !notification.persistent)
        .forEach(notification => {
          // Only set timer if one doesn't already exist for this notification
          if (!timersRef.current.has(notification.id)) {
            const timer = setTimeout(() => {
              if (handleDismissRef.current) {
                handleDismissRef.current(notification.id);
              }
              timersRef.current.delete(notification.id);
            }, autoHideDuration);
            timersRef.current.set(notification.id, timer);
          }
        });
    }

    return () => {
      // Cleanup on unmount or when notifications change
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, [visibleNotifications.length, autoHideDuration]);

  const handleDismiss = React.useCallback((id) => {
    // Clear timer if exists
    if (timersRef.current.has(id)) {
      clearTimeout(timersRef.current.get(id));
      timersRef.current.delete(id);
    }
    
    setVisibleNotifications(prev => prev.filter(n => n.id !== id));
    if (onDismiss) {
      onDismiss(id);
    }
    if (onClose) {
      onClose();
    }
  }, [onDismiss, onClose]);

  // Store latest handleDismiss in ref for use in useEffect
  useEffect(() => {
    handleDismissRef.current = handleDismiss;
  }, [handleDismiss]);

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'top-center':
        return 'top-4 left-1/2 transform -translate-x-1/2';
      case 'top-right':
        return 'top-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-center':
        return 'bottom-4 left-1/2 transform -translate-x-1/2';
      case 'bottom-right':
        return 'bottom-4 right-4';
      default:
        return 'top-4 right-4';
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-success',
          text: 'text-success-foreground',
          icon: 'CheckCircle',
          border: 'border-success/20'
        };
      case 'warning':
        return {
          bg: 'bg-warning',
          text: 'text-warning-foreground',
          icon: 'AlertTriangle',
          border: 'border-warning/20'
        };
      case 'error':
        return {
          bg: 'bg-error',
          text: 'text-error-foreground',
          icon: 'AlertCircle',
          border: 'border-error/20'
        };
      case 'info':
      default:
        return {
          bg: 'bg-primary',
          text: 'text-primary-foreground',
          icon: 'Info',
          border: 'border-primary/20'
        };
    }
  };

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div className={`fixed z-[1300] ${getPositionClasses()}`}>
      <div className="space-y-2 w-80 max-w-sm">
        {visibleNotifications.map((notification, index) => {
          const typeStyles = getTypeStyles(notification.type);
          
          return (
            <div
              key={notification.id}
              className={`
                bg-card border ${typeStyles.border} rounded-lg shadow-lg
                transform transition-all duration-300 ease-out
                animate-slide-in
              `}
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`${typeStyles.bg} ${typeStyles.text} rounded-full p-1 flex-shrink-0`}>
                    <Icon name={typeStyles.icon} size={16} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        {notification.title && (
                          <h4 className="text-sm font-medium text-card-foreground mb-1">
                            {notification.title}
                          </h4>
                        )}
                        <p className="text-sm text-muted-foreground">
                          {notification.message}
                        </p>
                        {notification.timestamp && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(notification.timestamp).toLocaleTimeString()}
                          </p>
                        )}
                      </div>

                      {/* Dismiss Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDismiss(notification.id)}
                        className="h-6 w-6 text-muted-foreground hover:text-foreground flex-shrink-0"
                      >
                        <Icon name="X" size={14} />
                      </Button>
                    </div>

                    {/* Action Buttons */}
                    {notification.actions && notification.actions.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {notification.actions.map((action, actionIndex) => (
                          <Button
                            key={actionIndex}
                            variant={action.variant || 'outline'}
                            size="sm"
                            onClick={() => {
                              if (action.onClick) {
                                action.onClick();
                              }
                              if (action.dismissOnClick !== false) {
                                handleDismiss(notification.id);
                              }
                            }}
                            className="text-xs"
                          >
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress Bar for Auto-dismiss */}
                {!notification.persistent && autoHideDuration > 0 && (
                  <div className="mt-3 h-1 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${typeStyles.bg} rounded-full animate-pulse`}
                      style={{
                        animation: `shrink ${autoHideDuration}ms linear forwards`
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default NotificationToast;